import type { Placement, PositioningStrategy, SideObject, State } from '@popperjs/core';
import type { SpringValues } from '@react-spring/web';
import type React from 'react';
import type { AriaAttributes, CSSProperties, MouseEventHandler, ReactNode } from 'react';
import type { Modifier } from 'react-popper';
import type { ReMap } from '../../utils/types';
export interface PopoverStateProps {
  closeOnClickOutside?: boolean;
  onClickOutside?: (e: MouseEvent | TouchEvent) => void;
  trigger?: 'click' | 'hover' | '';
  isSmall?: boolean;
  usePortal?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  placement?: Placement;
  /** Delay before opening and closing the popover when the mouse enters the target */
  delay?: number;
  /** Delay before closing the popover when the mouse leaves the target, overrides the delay */
  hideDelay?: number;
  modifiers?: Modifier<string, object>[];
  strategy?: PositioningStrategy;
  preventOverflow?: boolean;
  noPaddingAndBorder?: boolean;
  /** Pass an alternative document for attaching click outside events to, used when component is rendered inside a popout */
  popoverDocument?: Document;
  /** Override the max height calculated by popper */
  maxHeight?: CSSProperties['maxHeight'];
}

export type PopoverProps = React.PropsWithChildren<
  ReMap<
    Omit<PopoverState, 'update' | 'close' | 'open' | 'toggle' | 'placement' | 'popoverDocument'> &
      Omit<PopoverContentProps, 'transitionStyle'>
  >
>;

export type PopoverState = {
  onClickTarget: ((e: Event) => void) | (() => void);
  onMouseEnterTarget: () => void;
  onMouseEnterContent: () => void;
  onMouseLeave: MouseEventHandler<HTMLDivElement>;
  onFocusTarget: () => void;
  onBlurTarget: () => void;
  targetRef: HTMLElement | null;
  contentRef: HTMLDivElement | null;
  setTargetRef: React.RefCallback<HTMLSpanElement>;
  setContentRef: React.RefCallback<HTMLDivElement>;
  tabIndex?: number;
  state: State | null;
  update: (() => Promise<Partial<State>>) | null;
  /** Amount of spacing around overflow prevention */
  spacing: number;
  overflow: SideObject;
  close: () => void;
  open: () => void;
  isOpen: boolean;
  toggle: () => void;
  placement?: Placement;
  isSmall: boolean;
  usePortal: boolean;
  portalElement?: HTMLElement;
  styles: { popper: CSSProperties };
  attributes: { [key: string]: { [key: string]: string } | undefined };
  preventOverflow?: boolean;
  noPaddingAndBorder: boolean;
  contentStyle?: CSSProperties;
  /** Optional to set a zIndex over complex components -
   * use {@see Z_INDEX} from kyoko/src/styles/layout.ts as a basis */
  zIndex?: number;
};

export interface PopoverContentProps extends Pick<PopoverState, 'zIndex'> {
  style?: CSSProperties;
  contentStyle?: CSSProperties;
  placement?: Placement;
  variant?: PopoverVariant;
  transitionStyle?: SpringValues<CSSProperties>;
  isSmall?: boolean;
  children: ReactNode;
  onMouseLeave?: MouseEventHandler<HTMLDivElement>;
  overflow?: SideObject;
  overflowY?: CSSProperties['overflowY'];
  targetStyle?: CSSProperties;
  noPaddingAndBorder?: boolean;
  maxHeight?: CSSProperties['maxHeight'];
  'aria-busy'?: AriaAttributes['aria-busy'];
  'data-testid'?: string;
}

export enum PopoverVariant {
  Default = 'DEFAULT',
  /** Primary variant is used for tours */
  Primary = 'PRIMARY',
}
